import { useQuery } from '@tanstack/react-query';
import type { z } from 'zod';

import type {
  GetUserByIdResponse,
  GetUsersRequestSchema,
  GetUsersResponse,
} from '@mai/platform-api/src/routes/users.schema';

import { type QueryOptions, apiClient } from './axios';

// Type definitions
export type GetUsersRequest = z.infer<typeof GetUsersRequestSchema>;

// API functions
export const fetchUsers = async (
  params?: GetUsersRequest,
): Promise<GetUsersResponse> => {
  const response = await apiClient.get('/users', { params });
  return response.data;
};

export const fetchUserById = async (
  userId: string,
): Promise<GetUserByIdResponse> => {
  const response = await apiClient.get(`/users/${userId}`);
  return response.data;
};

export const fetchUsersByIds = async (
  userIds: string[],
): Promise<GetUsersResponse> => {
  if (userIds.length === 0)
    return {
      items: [],
      pagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  return fetchUsers({ userIds });
};

// React Query hooks
export const useUsers = (params?: GetUsersRequest, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => fetchUsers(params),
    enabled: options?.enabled,
    retry: options?.retry,
  });
};

export const useUserById = (userId: string | null, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => fetchUserById(userId!),
    enabled: !!userId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useUsersByIds = (userIds: string[], options?: QueryOptions) => {
  return useQuery({
    queryKey: ['users', 'by-ids', userIds],
    queryFn: () => fetchUsersByIds(userIds),
    enabled: userIds.length > 0 && options?.enabled !== false,
    retry: options?.retry,
  });
};
